<script>
export default {
  name: 'RoundRadioButton',
  props: {
    item: String,
    name: String,
    checked: Boolean,
    subtext: {
      type: String,
      default: '',
    },
    customClass: {
      type: String,
      default: '',
    },
    subtextClass: {
      type: String,
      default: '',
    },
    show: {
      type: Boolean,
      default: true,
    },
    showBorderOnHover: {
      type: Boolean,
      default: true,
    },
    value: [String, Boolean, Number],
    handleClick: {
      type: Function,
      default: () => {},
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    displayAsRequired: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'small',
      validator: (value) => ['small', 'medium', 'large'].includes(value),
    },
    showBackgroundWhenSelected: {
      type: Boolean,
      default: true,
    },
  },
};
</script>

<template>
  <div
    class="flex flex-grow p-1 transition-colors duration-200"
    :class="[
      show && checked && showBackgroundWhenSelected
        ? 'activeRadioButton'
        : show && checked && !showBackgroundWhenSelected
          ? 'activeRadioButtonNoBackground'
          : displayAsRequired && show && !disabled
            ? 'border-red-100 bg-red-100 hover:bg-off-white'
            : displayAsRequired && show && disabled
              ? 'border-red-100 bg-red-100'
              : 'border-transparent',
      showBorderOnHover && !disabled
        ? 'border-l-4 border-transparent hover:border-light-gray'
        : showBorderOnHover && disabled
          ? 'border-l-4 border-transparent'
          : 'rounded-[5px]',
    ]"
  >
    <label
      class="container-radio group w-full"
      :class="{
        'text-dark-gray': show && checked,
        disabled: disabled,
        size,
      }"
    >
      <slot />
      <div class="flex flex-col">
        <div class="flex items-center gap-0.5">
          <slot name="item-icon" />
          <p
            class="text-1md"
            :class="{ 'text-dark-gray': checked, [customClass]: customClass }"
          >
            {{ item }}
          </p>
        </div>
        <p
          v-if="!!subtext.length"
          class="text-xxs leading-3"
          :class="
            checked
              ? `text-dark-gray ${subtextClass}`
              : `text-body-color ${subtextClass}`
          "
          style="margin-top: 3px; margin-bottom: 3px"
        >
          {{ subtext }}
        </p>
      </div>
      <input
        type="radio"
        :value="value"
        :name="name"
        :checked="show && checked"
        :disabled="disabled"
        @click="() => (disabled ? null : handleClick(value))"
      />
      <span
        v-if="!showBorderOnHover"
        class="checkmark border-2 border-transparent"
        :class="[
          !(show && checked) && !disabled
            ? 'group-hover:bg-steel-300'
            : 'checked-no-hover-border',
        ]"
      />
    </label>
  </div>
</template>

<style scoped>
/* Custom CSS for Checkboxes */
/* Template used from W3 schools https://www.w3schools.com/howto/tryit.asp?filename=tryhow_css_custom_checkbox */
/* The container */
.container-radio {
  @apply relative block flex h-full cursor-pointer items-center pl-8 text-left text-sm text-body-color;
  min-height: 40px;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

/* Size variants */
.container-radio.small {
  @apply pl-8;
  min-height: 40px;
}

.container-radio.medium {
  @apply pl-12;
  min-height: 60px;
}

.container-radio.large {
  @apply pl-16;
  min-height: 80px;
}

/* Hide the browser's default checkbox */
.container-radio input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

/* Create a custom checkbox */
.checkmark {
  @apply absolute left-2 h-4 w-4 rounded-full border-2 border-transparent bg-light-gray;
  top: 20px;
  transform: translate(0, -50%);
}

/* Size variants for checkmark */
.small .checkmark {
  @apply h-4 w-4;
  top: 20px;
}

.medium .checkmark {
  @apply h-6 w-6;
  top: 30px;
}

.large .checkmark {
  @apply h-8 w-8;
  top: 40px;
}

/* When the checkbox is checked, add a yellow background */
.container-radio input:checked ~ .checkmark {
  @apply border-transparent bg-yellow-500;
}

/* When the checkbox is not checked, show border */
.container-radio input:not(:checked) ~ .checkmark {
  @apply border-steel-300;
}

/* Prevent hover border on checked radio buttons */
.container-radio input:checked ~ .checkmark:hover {
  @apply border-transparent !important;
}

/* Create the checkmark/indicator (hidden when not checked) */
.checkmark:after {
  content: '';
  position: absolute;
  display: none;
}

/* Show the checkmark when checked */
.container-radio input:checked ~ .checkmark:after {
  display: block;
}

/* Style the checkmark/indicator */
.container-radio .checkmark:after {
  content: '';
  position: absolute;
  display: none;
  width: 0.375rem;
  height: 0.375rem;
  background-color: #000;
  border: solid #000;
  border-radius: 9999px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.activeRadioButton {
  @apply border-yellow-500 bg-light-gray;
}

.activeRadioButtonNoBackground {
  @apply border-yellow-500;
}

/* Custom hover effect for radio button container */
.container-radio:hover {
  transition: background-color 0.2s ease;
}

/* Disable hover effects when radio button is disabled */
.container-radio.disabled:hover {
  background-color: transparent;
  cursor: not-allowed !important;
}

.container-radio.disabled .checkmark:hover {
  background-color: inherit;
}

.container-radio input:disabled ~ .checkmark {
  opacity: 0.5;
  cursor: not-allowed;
}

.container-radio input:disabled {
  cursor: not-allowed;
}

.container-radio.disabled {
  cursor: not-allowed !important;
  opacity: 0.6;
}
</style>
